import {
  getInputProps,
  getTextareaProps,
  FieldMetadata,
  getSelectProps,
} from "@conform-to/react";
import * as RadixForm from "@radix-ui/react-form";

import { ValidationMessage } from "./validation-message";

import { TextField, Checkbox, Flex, TextArea } from "@radix-ui/themes";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./form-select";

export function FormInput({
  field,
  type,
  label,
  direction = "column",
  disabled,
}: {
  field: FieldMetadata;
  type: "text" | "date" | "email" | "password";
  label?: string;
  direction?: "column" | "row";
  disabled?: boolean;
}) {
  return (
    <RadixForm.Field name={field.name} className="relative">
      <Flex direction={direction} gap="2">
        {label && (
          <RadixForm.Label htmlFor={field.id} className="font-bold">
            {label}
          </RadixForm.Label>
        )}
        <RadixForm.Control asChild className="w-full h-10">
          <TextField.Root
            radius="large"
            {...getInputProps(field, { type })}
            disabled={disabled}
          />
        </RadixForm.Control>
        <ValidationMessage field={field} />
      </Flex>
    </RadixForm.Field>
  );
}

export function FormTextarea({
  field,
  label,
  disabled,
}: {
  field: FieldMetadata;
  label: string;
  disabled?: boolean;
}) {
  return (
    <RadixForm.Field {...getTextareaProps(field)} className="relative">
      <Flex direction="column" gap="2">
        <label htmlFor={field.id} className="font-bold">
          {label}
        </label>
        <TextArea
          radius="large"
          {...getTextareaProps(field)}
          resize="vertical"
          size="3"
          disabled={disabled}
        />
        <ValidationMessage field={field} />
      </Flex>
    </RadixForm.Field>
  );
}

export function FormCheckbox({
  field,
  label,
  disabled,
}: {
  field: FieldMetadata;
  label: string;
  disabled?: boolean;
}) {
  return (
    <RadixForm.Field name={field.name} className="relative">
      <Flex direction="column" gap="2">
        <Flex className="w-full h-10" align={"center"} gap="2">
          <Checkbox
            id={field.id}
            name={field.name}
            defaultChecked={field.initialValue === "on"}
            disabled={disabled}
          />
          <label htmlFor={field.id} className="font-bold">
            {label}
          </label>
        </Flex>
        <ValidationMessage field={field} />
      </Flex>
    </RadixForm.Field>
  );
}

export function FormSelect({
  field,
  label,
  disabled,
  defaultValue,
  options,
  placeholder,
}: {
  field: FieldMetadata;
  label: string;
  disabled?: boolean;
  defaultValue?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <RadixForm.Field name={field.name} className="relative" asChild>
      <Flex direction="column" gap="2">
        <Select
          {...getSelectProps(field)}
          defaultValue={defaultValue}
          disabled={disabled}
        >
          <label htmlFor={field.id} className="font-bold">
            {label}
          </label>
          <SelectTrigger>
            <SelectValue placeholder={placeholder ? placeholder : "未選択"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectGroup key={option.value} id={field.id}>
                <SelectItem value={option.value}>{option.label}</SelectItem>
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <ValidationMessage field={field} />
      </Flex>
    </RadixForm.Field>
  );
}
